import produce from 'immer';
import isPlainObject from 'lodash/isPlainObject';

/**
 * Standard function to create Redux reducer.
 *
 * `createReducer` simplifies reducer creation and more importantly,
 * _it ensures immutability_. So direct modification to state object is allowed.
 * See https://github.com/immerjs/immer.
 *
 * Concretely, it turns reducer creation with a huge switch block into
 *
 * ```javascript
 * const initialState = {}
 * const reducer = createReducer(initialState, {
 *   ACTION_1: (draft, action) => {
 *     draft.count++ // direct modification is allowed
 *     return draft // returning is optional
 *   },
 *   ACTION_2: (draft, action) => {}
 * })
 * ```
 *
 * @param {*} initialState Initial state of the reducer.
 * @param {Object<string, Function>} handlers An action-handler object hash. Each handler function receives `state` and `action`.
 * @return {Function} The reducer.
 */
function createReducer(initialState, handlers) {
  // Sanity checks
  if (!handlers) {
    throw new Error('Missing `handlers` in `createReducer`.');
  }
  if (!isPlainObject(handlers)) {
    throw new Error(
      `Expect \`handlers\` to be an object but got ${typeof handlers} instead.`
    );
  }

  // The reducer!!!
  return function reducer(state = initialState, action) {
    // Get the correct handler to handle action
    if (Object.prototype.hasOwnProperty.call(handlers, action.type)) {
      const handler = handlers[action.type];
      return produce(state, draft => handler(draft, action));
    }

    // If we get here, return existing state
    return state;
  };
}

export default createReducer;
